import { getClient } from '../getClient'
import { getLocaleRegionIdFromPath } from '@aether/utils'
import { groq } from 'next-sanity'
import { createSEOProjection } from '../projections/createSEOProjection'
import { createEventProjection } from '../projections/createEventProjection'
import { MediaFieldType, PageLink } from '@aether/models'
import { getIsDateInPDTInPast } from '@aether/utils'

export interface EventData {
  _id: string
  title?: string
  startDate?: string
  endDate?: string
  locationShort?: string
  media?: MediaFieldType
  handle?: string
  rsvpEndDate?: string
  primaryCta?: {
    link?: PageLink
    label?: string
    moreInformation?: string
  }
  isVipEvent?: boolean
  isEventEnded?: boolean
  handleOverrideLink?: PageLink
}

export interface EventsPageData {
  featuredEvents: Array<EventData>
  pastEvents: Array<EventData>
}

type EventDataWithoutComputedFields = Omit<EventData, 'isEventEnded'>

const isEventInFuture = (event: EventDataWithoutComputedFields) => {
  return !getIsDateInPDTInPast(event.endDate || event.startDate)
}

export const getEventsPage = async (
  locale: string,
  preview = false,
): Promise<EventsPageData | null> => {
  const [, localeId] = getLocaleRegionIdFromPath(locale)
  const SEO_PROJECTION = createSEOProjection(localeId)
  const EVENT_PROJECTION = createEventProjection(localeId)

  const query = groq`*[_type == "eventsPage"][0]{
    "seo": seo${SEO_PROJECTION},
    "featuredEvents": featuredEvents[]-> {
      ${EVENT_PROJECTION}
    },
    "pastEvents": pastEvents[]-> {
      ${EVENT_PROJECTION}
    }
  }`

  const page = await getClient(preview).fetch(query)

  if (!page) {
    return null
  }

  return {
    ...page,
    featuredEvents:
      page?.featuredEvents
        ?.filter(isEventInFuture)
        .map((event: EventDataWithoutComputedFields) => ({
          ...event,
          isEventEnded: false,
        })) ?? [],
    pastEvents:
      page?.pastEvents?.map((event: EventDataWithoutComputedFields) => ({
        ...event,
        isEventEnded: true,
      })) ?? [],
  }
}
